import { BulkLimitDialog } from '@aibees/shared/components';
import { BaseEntity } from '@aibees/shared/definition';
import { useCurrentSubscriptionApi } from '@aibees/shared/features/price-and-plan/api/use-current-subscription-api';
import { useBoolean, useUnMount } from '@aibees/shared/hooks';
import {
  arraysAreEqual,
  classnames,
  fHumanReadableNumber,
  toNumber,
} from '@aibees/shared/utils';
import { useIsInternalUser } from '@aibees/shared/workspace/hooks/use-is-internal-user';
import { Divider } from '@mui/material';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useAdvanceTableContext } from '../../context';
import { AdvancedTableSelectPopupActionButtons } from './components/advanced-table-select-popup-action-buttons';
import { AdvancedTableSelectPopupAllOption } from './components/advanced-table-select-popup-all-option';
import { AdvancedTableSelectPopupError } from './components/advanced-table-select-popup-error';
import { AdvancedTableSelectPopupNumberOption } from './components/advanced-table-select-popup-number-option';
import { AdvancedTableSelectPopupPageOption } from './components/advanced-table-select-popup-page-option';
import { useCheckboxState } from './hooks/use-checkbox-state';
import { useExportSizeLimit } from './hooks/use-export-size-limit';
import * as Styled from './styles';
import * as SX from './sx';
import { AdvancedTableSelectPopupProps as Props, SelectionMode } from './types';

export const AdvancedTableSelectionPopover = memo(
  <T extends BaseEntity>({
    className,
    table,
    data,
    additionalContent,
  }: Props<T>) => {
    const limitDialogState = useBoolean();
    const disabledSubmitButtonState = useBoolean();
    const { config } = useAdvanceTableContext();
    const { data: subscriptionData } = useCurrentSubscriptionApi();
    const internalUser = useIsInternalUser();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [maxPeople, setMaxPeople] = useState<number | string>('1');
    const [mode, setMode] = useState<SelectionMode | undefined>(undefined);
    const [page, setPage] = useState<number | undefined>();
    const [selectedRecordCount, setSelectedRecordCount] = useState<number>(0);
    const [applyError, setApplyError] = useState<string | undefined>();
    const selectedItemsCount = useMemo(
      () => table?.selection.count ?? 0,
      [table?.selection.count]
    );
    const [targetRecordCount, setTargetRecordCount] = useState<number>(
      +(selectedItemsCount ?? 0)
    );

    const open = Boolean(anchorEl);
    const sizeLimit = useExportSizeLimit(data?.totalElements);

    // Memoize items from data
    const items = useMemo(() => data?.content || [], [data?.content]);
    const selectedIds = useMemo(() => items?.map((x) => x.id) || [], [items]);

    // Calculate current page size including similarity search results (top)
    const currentPageSize = useMemo(
      () => (data?.numberOfElements ?? 0) + (data?.top?.length ?? 0),
      [data?.numberOfElements, data?.top?.length]
    );
    const errorMessage = useMemo(() => {
      // Apply error takes priority (only shown when handleApply is called)
      if (applyError) {
        return applyError;
      }

      const maxPeopleChecked = table?.selection.extraParam?.maxPeopleChecked;

      // Max people validation errors (real-time)
      if (maxPeopleChecked) {
        if (maxPeople === '') {
          return 'Please enter a Number';
        }
        if (maxPeople === '0') {
          return 'Value must be greater than 0';
        }
        if (targetRecordCount > 0 && +maxPeople > targetRecordCount) {
          return "Can't exceed selected people records.";
        }
      }

      return undefined;
    }, [
      applyError,
      maxPeople,
      targetRecordCount,
      table?.selection.extraParam?.maxPeopleChecked,
    ]);
    const disabledSubmitButton = useMemo(() => {
      return mode === SelectionMode.PAGE &&
        !arraysAreEqual(selectedIds, table?.selection.included ?? []) &&
        page !== data?.number &&
        !errorMessage
        ? false
        : targetRecordCount === 0 ||
            (targetRecordCount === selectedRecordCount &&
              !table?.selection.extraParam?.maxPeopleChecked &&
              targetRecordCount === selectedItemsCount) ||
            (!!table?.selection.extraParam?.maxPeopleChecked &&
              !!table?.selection.extraParam?.maxPeople &&
              targetRecordCount === selectedRecordCount &&
              +maxPeople === table?.selection.extraParam?.maxPeople) ||
            +maxPeople === 0 ||
            !!errorMessage;
    }, [
      mode,
      selectedIds,
      table?.selection.included,
      table?.selection.extraParam?.maxPeopleChecked,
      table?.selection.extraParam?.maxPeople,
      page,
      data?.number,
      errorMessage,
      targetRecordCount,
      selectedRecordCount,
      selectedItemsCount,
      maxPeople,
    ]);

    const disabledClearButton = useMemo(() => {
      return (
        targetRecordCount === 0 &&
        !table?.selection.extraParam?.maxPeopleChecked
      );
    }, [targetRecordCount, table?.selection.extraParam?.maxPeopleChecked]);

    // Calculate error message based on current state

    // Clear selection
    const handleClear = useCallback(() => {
      table?.selection.onClear();
      table?.selection.onRemoveExtraParam('maxPeople');
      table?.selection.onRemoveExtraParam('maxPeopleChecked');
      setMaxPeople(1);
      setTargetRecordCount(0);
      setSelectedRecordCount(0);
      setMode(undefined);
      setApplyError(undefined);
      disabledSubmitButtonState.onFalse();
    }, [table?.selection, disabledSubmitButtonState]);

    // Select current page rows
    const handleSelectPage = useCallback(() => {
      setTargetRecordCount(currentPageSize);
      setMode(SelectionMode.PAGE);
      setApplyError(undefined);

      if (maxPeople !== '0' && maxPeople !== '') {
        disabledSubmitButtonState.onFalse();
      }
    }, [currentPageSize, disabledSubmitButtonState, maxPeople]);

    // Select all rows
    const handleSelectAll = useCallback(() => {
      setTargetRecordCount(sizeLimit.normalRowsLimit);
      setMode(SelectionMode.ALL);
      setApplyError(undefined);
      if (maxPeople !== '0' && maxPeople !== '') {
        disabledSubmitButtonState.onFalse();
      }
    }, [disabledSubmitButtonState, maxPeople, sizeLimit.normalRowsLimit]);

    // Select number of rows input handler

    // Memoize input change handler
    const handleSelectedRecordCountChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = +e.target.value;
        // Reject negatives — inputProps.min only sets native validation,
        // it does not block typing "-5". State is controlled, so ignoring
        // the change reverts the input to the last valid value.
        if (val < 0) {
          return;
        }
        setMode(SelectionMode.NUMBER);
        if (val <= sizeLimit.normalRowsLimit) {
          disabledSubmitButtonState.onFalse();
        }
        // Clear apply error when targetRecordCount changes
        setApplyError(undefined);

        setTargetRecordCount(val);
      },
      [disabledSubmitButtonState, sizeLimit.normalRowsLimit]
    );

    const handleClose = useCallback(() => setAnchorEl(null), []);

    const handleMaxPeopleChange = useCallback(
      (val: string) => {
        if (+val <= targetRecordCount) {
          disabledSubmitButtonState.onFalse();
        }
        // Clear apply error when maxPeople changes
        setApplyError(undefined);

        setMaxPeople(val);
      },
      [disabledSubmitButtonState, targetRecordCount]
    );

    const handleMaxPeopleCheckedChange = useCallback(
      (checked: boolean) => {
        if (!checked && targetRecordCount <= sizeLimit.normalRowsLimit) {
          disabledSubmitButtonState.onFalse();
        }
      },
      [targetRecordCount, sizeLimit.normalRowsLimit, disabledSubmitButtonState]
    );

    const handleApply = useCallback(() => {
      // Clear any previous apply error
      setApplyError(undefined);

      if (
        targetRecordCount > 25 &&
        sizeLimit.normalRowsLimit > 25 &&
        config.paidBulkSelection &&
        subscriptionData?.current?.plan?.metadata.is_free &&
        !internalUser
      ) {
        limitDialogState.onTrue();
        setTargetRecordCount(25);
        return;
      }
      if (
        +maxPeople > targetRecordCount &&
        table?.selection.extraParam?.maxPeopleChecked
      ) {
        disabledSubmitButtonState.onTrue();
        return;
      }

      if (targetRecordCount > sizeLimit.normalRowsLimit) {
        if (sizeLimit.systemLimitReached) {
          setApplyError(
            `Max ${fHumanReadableNumber(
              sizeLimit.normalRowsLimit
            )} records for performance.`
          );
          disabledSubmitButtonState.onTrue();
          return;
        } else {
          setApplyError("Can't exceed selected people records.");
          disabledSubmitButtonState.onTrue();
          return;
        }
      }

      if (mode === SelectionMode.PAGE) {
        const maxPeopleValue = table?.selection.extraParam?.maxPeopleChecked
          ? +maxPeople
          : undefined;
        table?.selection.onSelectRowsById(items, maxPeopleValue);
      } else {
        table?.selection.onSelectRowsBySize(targetRecordCount);
      }
      setPage(data?.number);

      setSelectedRecordCount(targetRecordCount);
      setAnchorEl(null);

      if (table?.selection.extraParam?.maxPeopleChecked && !!+maxPeople)
        table?.selection.onSetExtraParam('maxPeople', +maxPeople);

      if (!table?.selection.extraParam?.maxPeopleChecked)
        table?.selection.onRemoveExtraParam('maxPeople');
    }, [
      targetRecordCount,
      sizeLimit.normalRowsLimit,
      sizeLimit.systemLimitReached,
      config.paidBulkSelection,
      subscriptionData,
      internalUser,
      maxPeople,
      table?.selection,
      mode,
      data?.number,
      limitDialogState,
      disabledSubmitButtonState,
      items,
    ]);

    const clonedAdditionalContent = useMemo(() => {
      if (additionalContent && React.isValidElement(additionalContent)) {
        return React.cloneElement(
          additionalContent as React.ReactElement<{
            maxPeople?: number | string;
            setMaxPeople?: (value: number | string) => void;
            onMaxPeopleChange?: (value: string) => void;
            onMaxPeopleCheckedChange?: (value: boolean) => void;
          }>,
          {
            maxPeople,
            setMaxPeople,
            onMaxPeopleChange: handleMaxPeopleChange,
            onMaxPeopleCheckedChange: handleMaxPeopleCheckedChange,
          }
        );
      }
      return additionalContent;
    }, [
      additionalContent,
      handleMaxPeopleChange,
      handleMaxPeopleCheckedChange,
      maxPeople,
    ]);

    // Get checkbox state (checked, indeterminate)
    const state = useCheckboxState(
      table?.selection.size ?? 0,
      table?.selection.included.length ?? 0,
      table?.selection.count
    );

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleNumberInputKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        // Block chars a type="number" input otherwise accepts but we don't want
        // (negative sign, exponent, plus). onChange guards value; this stops the
        // key from ever reaching the field so no stray "-" flashes in.
        if (['-', '+', 'e', 'E'].includes(e.key)) {
          e.preventDefault();
          return;
        }
        if (e.key === 'Enter' && !!targetRecordCount) {
          handleApply();
        }
      },
      [handleApply, targetRecordCount]
    );

    useUnMount(() => {
      setMode(undefined);
    });

    useEffect(() => {
      if (page !== data?.number) {
        setMode(undefined);
      }
    }, [page, data?.number]);

    return (
      <>
        <BulkLimitDialog
          state={limitDialogState}
          imgSrc="/assets/images/upgradeToBulk.png"
          title="Upgrade to Bulk Select Records"
        />

        <Styled.Wrapper
          className={classnames('AdvancedTableSelectionPopover', className)}
          sx={SX.root}
          onClick={handleClick}
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          count={table?.selection.count}
          disabled={!items.length}
          checkboxDisabled={false}
          state={state}
          open={open}
        />

        <Styled.MenuList
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          padding="none"
        >
          <Styled.ContentWrapper>
            <AdvancedTableSelectPopupPageOption
              size={currentPageSize}
              handleClick={handleSelectPage}
            />
          </Styled.ContentWrapper>
          <Divider />

          <Styled.ContentWrapper>
            <AdvancedTableSelectPopupAllOption
              sizeLimit={sizeLimit}
              onClick={handleSelectAll}
            />
          </Styled.ContentWrapper>
          <Divider />

          <Styled.ContentWrapper>
            <AdvancedTableSelectPopupNumberOption
              onKeyDown={handleNumberInputKeyDown}
              value={
                toNumber(targetRecordCount) === 0
                  ? ''
                  : toNumber(targetRecordCount)
              }
              max={sizeLimit.normalRowsLimit}
              extra={data?.top?.length}
              handleChange={handleSelectedRecordCountChange}
            />
          </Styled.ContentWrapper>

          {clonedAdditionalContent && <Divider />}

          {clonedAdditionalContent && (
            <Styled.ContentWrapper>
              {clonedAdditionalContent}
            </Styled.ContentWrapper>
          )}

          {errorMessage && (
            <AdvancedTableSelectPopupError errorText={errorMessage} />
          )}

          <AdvancedTableSelectPopupActionButtons
            onApply={handleApply}
            onClear={handleClear}
            disabledSubmitButton={
              disabledSubmitButton || disabledSubmitButtonState.value
            }
            disabledClearButton={disabledClearButton}
          />
        </Styled.MenuList>
      </>
    );
  }
);
